import { memo } from 'react';
import { AlignLeft, Ban, Link2, CheckSquare, Clock, Flag, Hourglass, MessageSquare, MoreHorizontal, Paperclip, Repeat, Star, Timer } from 'lucide-react';
import { approvalStatus, dueState, formatDue, formatFieldValue, formatMinutes, type CardSummary, type CustomField, type Density, type Label, type Member } from '@kaba/shared';
import { Avatar, cx, LABEL_BG } from '../ui/ui';

const PRIORITY_COLOR = ['', 'text-slate-400', 'text-amber-500', 'text-orange-600', 'text-rose-600'];
const PRIORITY_NAME = ['', 'Niedrig', 'Mittel', 'Hoch', 'Dringend'];

export interface CardTileProps {
  card: CardSummary;
  labels: Map<string, Label>;
  members: Map<string, Member>;
  /** Eigene Felder, die auf der Kartenvorderseite erscheinen */
  fields?: CustomField[];
  showCover?: boolean;
  dragging?: boolean;
  overlay?: boolean;
  /** Kompakt: ohne Bild und Nebeninfos · Standard · Visuell: großes Cover */
  density?: Density;
  /** Andere Personen, die die Karte gerade offen haben */
  viewers?: Member[];
  /** Rendert den Aktionen-Knopf (erhält, ob ein Vollbild-Cover aktiv ist) */
  menu?: (card: CardSummary, onImage: boolean) => React.ReactNode;
  /** Anzahl offener Karten, auf die diese Karte wartet */
  waiting?: number;
  /** Terminkonflikt mit einer Vorgängerkarte */
  conflict?: boolean;
}

export function menuButtonClass(onImage: boolean) {
  return cx(
    '-mt-1 -mr-1 inline-flex size-8 shrink-0 items-center justify-center rounded-md',
    onImage ? 'text-white/90 hover:bg-white/20' : 'text-[var(--ink-soft)] hover:bg-black/5 dark:hover:bg-white/10',
    'opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100',
  );
}

export { MoreHorizontal };

export const CardTile = memo(function CardTile({
  card,
  labels,
  members,
  fields,
  showCover = true,
  dragging,
  overlay,
  density = 'standard',
  viewers,
  menu,
  waiting = 0,
  conflict = false,
}: CardTileProps) {
  const due = card.dueAt ? dueState(card.dueAt, card.dueHasTime, card.completedAt) : 'none';
  const compact = density === 'compact';
  const visual = density === 'visual';
  const cover = showCover ? card.cover : null;
  // Kompakt: Bild-Cover nur als Farbstreifen, kein Vollbild
  const full = cover?.size === 'full' && !compact;
  const cardLabels = card.labelIds.map((id) => labels.get(id)).filter((l): l is Label => !!l);
  // Hauptverantwortlich zuerst
  const assignees = [...card.assigneeIds]
    .sort((a, b) => Number(b === card.ownerId) - Number(a === card.ownerId))
    .map((id) => members.get(id))
    .filter((m): m is Member => !!m);

  const imageCover =
    cover?.type === 'image' && cover.url && !compact ? (
      <img
        src={cover.url}
        srcSet={cover.srcSet ?? undefined}
        sizes="(max-width: 640px) 85vw, 280px"
        alt={cover.alt ?? ''}
        draggable={false}
        loading="lazy"
        decoding="async"
        className={cx('block w-full object-cover', full ? 'absolute inset-0 h-full' : visual ? 'aspect-[4/3]' : 'h-24')}
        style={{
          objectPosition: `${(cover.focusX ?? 0.5) * 100}% ${(cover.focusY ?? 0.5) * 100}%`,
          backgroundImage: cover.placeholder ? `url(${cover.placeholder})` : undefined,
          backgroundSize: 'cover',
        }}
      />
    ) : null;

  return (
    <div
      className={cx(
        'kaba-card group relative overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--panel)] text-left shadow-[0_1px_0_rgba(0,0,0,0.04)]',
        dragging && 'opacity-40',
        overlay && 'rotate-[2deg] scale-[1.03] shadow-2xl ring-2 ring-accent/50 cursor-grabbing',
        full && cover?.type === 'image' && 'min-h-40 text-white',
        card.completedAt && 'opacity-75',
      )}
    >
      {cover?.type === 'color' && !full && <div className={cx(compact ? 'h-1.5' : visual ? 'h-16' : 'h-8', LABEL_BG[cover.color ?? 'gray'])} />}
      {cover?.type === 'image' && compact && <div className="h-1.5 bg-slate-400/60" />}
      {cover?.type === 'color' && full && <div className={cx('absolute inset-0', LABEL_BG[cover.color ?? 'gray'])} />}
      {imageCover}
      {full && cover?.type === 'image' && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
      )}

      <div className={cx('relative flex flex-col', compact ? 'gap-1 px-2.5 py-2' : 'gap-1.5 p-2.5', full && 'min-h-40 justify-end', full && visual && 'min-h-56')}>
        {cardLabels.length > 0 && compact && (
          <div className="flex flex-wrap gap-1" aria-label={cardLabels.map((l) => l.name).filter(Boolean).join(', ') || undefined}>
            {cardLabels.map((l) => (
              <span key={l.id} className={cx('h-1.5 w-6 rounded-full', LABEL_BG[l.color])} title={l.name || undefined} />
            ))}
          </div>
        )}
        {cardLabels.length > 0 && !compact && (
          <div className="flex flex-wrap gap-1">
            {cardLabels.map((l) => (
              <span
                key={l.id}
                className={cx('h-2 min-w-8 rounded-full', LABEL_BG[l.color], l.name && 'h-auto px-1.5 py-px text-[11px] font-medium text-white')}
                title={l.name || undefined}
              >
                {l.name}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-start gap-1">
          <p
            className={cx(
              'flex-1 text-[15px] leading-snug break-words sm:text-sm',
              compact ? 'line-clamp-2' : 'line-clamp-4',
              card.completedAt && 'line-through decoration-1',
            )}
          >
            {card.title}
          </p>
          {menu && !overlay && menu(card, full)}
        </div>

        {card.blocked && (
          <div className="flex items-center gap-1 rounded-md bg-rose-600/90 px-1.5 py-0.5 text-xs font-medium text-white">
            <Ban size={13} className="shrink-0" />
            <span className="truncate">Blockiert{card.blockedReason ? `: ${card.blockedReason}` : ''}</span>
          </div>
        )}

        {!compact && <FieldChips card={card} fields={fields} members={members} full={full} />}
        {viewers && viewers.length > 0 && (
          <div className={cx('flex items-center gap-1.5 text-xs', full ? 'text-white/90' : 'text-emerald-700 dark:text-emerald-400')}>
            <span className="flex -space-x-1">
              {viewers.slice(0, 3).map((v) => (
                <Avatar key={v.id} user={v} size={18} online />
              ))}
            </span>
            <span className="truncate">{viewers.length === 1 ? `${viewers[0]!.name} ist hier` : `${viewers.length} Personen sind hier`}</span>
          </div>
        )}
        <Badges card={card} due={due} full={full} assignees={assignees} compact={compact} waiting={waiting} conflict={conflict} />
      </div>
    </div>
  );
});

/** 0.80: Freigabe auf der Karte farbig (grün freigegeben, gelb teilweise, rot abgelehnt) */
const APPROVAL_CHIP = {
  open: 'bg-black/5 text-[var(--ink-soft)] dark:bg-white/10',
  partial: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
  approved: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100',
  rejected: 'bg-rose-100 text-rose-900 dark:bg-rose-900/40 dark:text-rose-100',
} as const;

export function FieldChips({
  card,
  fields,
  members,
  full,
}: {
  card: CardSummary;
  fields?: CustomField[];
  members: Map<string, Member>;
  full?: boolean;
}) {
  if (!fields?.length) return null;
  const chips = fields
    .map((f) => ({ f, text: formatFieldValue(f, card.custom?.[f.id], members) }))
    .filter((x): x is { f: CustomField; text: string } => !!x.text);
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {chips.map(({ f, text }) => (
        <span
          key={f.id}
          title={f.name}
          className={cx(
            'max-w-full truncate rounded-md px-1.5 py-px text-[11px]',
            full ? 'bg-white/20 text-white' : f.type === 'approval' ? APPROVAL_CHIP[approvalStatus(card.custom?.[f.id])] : 'bg-black/5 text-[var(--ink-soft)] dark:bg-white/10',
          )}
        >
          <span className="opacity-70">{f.name}:</span> {text}
        </span>
      ))}
    </div>
  );
}

function Badges({
  card,
  due,
  full,
  assignees,
  compact,
  waiting = 0,
  conflict = false,
}: {
  card: CardSummary;
  due: ReturnType<typeof dueState>;
  full: boolean;
  assignees: Member[];
  compact?: boolean;
  waiting?: number;
  conflict?: boolean;
}) {
  const hasAny =
    waiting ||
    card.dueAt ||
    card.checklistTotal ||
    card.attachmentCount ||
    card.commentCount ||
    card.recurrence ||
    card.timerUserIds.length ||
    card.estimateMinutes ||
    card.hasDescription ||
    card.priority ||
    assignees.length;
  if (!hasAny) return null;
  const soft = full ? 'text-white/85' : 'text-[var(--ink-soft)]';
  return (
    <div className={cx('flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs', soft)}>
      {waiting > 0 && (
        <span
          className={cx('inline-flex items-center gap-1 rounded px-1 py-px font-medium', conflict ? 'bg-amber-400 text-amber-950' : 'bg-black/5 dark:bg-white/10')}
          title={conflict ? `Wartet auf ${waiting} offene Karte(n) – Terminkonflikt` : `Wartet auf ${waiting} offene Karte(n)`}
        >
          <Link2 size={12} /> {waiting}
          <span className="sr-only">{conflict ? ` (wartet, Terminkonflikt)` : ' (wartet)'}</span>
        </span>
      )}
      {card.dueAt && (
        <span
          className={cx(
            'inline-flex items-center gap-1 rounded px-1 py-px font-medium',
            due === 'overdue' && 'bg-rose-600 text-white',
            due === 'soon' && 'bg-amber-400 text-amber-950',
            due === 'done' && 'bg-emerald-600 text-white',
          )}
          title={due === 'overdue' ? 'Überfällig' : due === 'soon' ? 'Fällig innerhalb von 24 Stunden' : 'Fällig'}
        >
          <Clock size={12} />
          {formatDue(card.dueAt, card.dueHasTime)}
          {due === 'overdue' && <span className="sr-only"> (überfällig)</span>}
        </span>
      )}
      {card.recurrence && (
        <span className="inline-flex items-center" title="Wiederkehrend">
          <Repeat size={12} />
          <span className="sr-only">Wiederkehrend</span>
        </span>
      )}
      {card.timerUserIds.length > 0 && (
        <span className="inline-flex items-center gap-1 rounded bg-emerald-600 px-1 text-white" title="Timer läuft">
          <Timer size={12} className="animate-pulse" />
          <span className="sr-only">Timer läuft</span>
        </span>
      )}
      {!compact && card.estimateMinutes ? (
        <span
          className={cx('inline-flex items-center gap-1', card.spentMinutes > card.estimateMinutes && 'font-semibold text-rose-600')}
          title="Erfasst / geschätzt"
        >
          <Hourglass size={12} /> {formatMinutes(card.spentMinutes).replace(' Std.', 'h').replace(' Min.', 'm')}/
          {formatMinutes(card.estimateMinutes).replace(' Std.', 'h').replace(' Min.', 'm')}
        </span>
      ) : null}
      {card.priority > 0 && (
        <span className={cx('inline-flex items-center', full ? '' : PRIORITY_COLOR[card.priority])} title={`Priorität: ${PRIORITY_NAME[card.priority]}`}>
          <Flag size={13} fill="currentColor" />
          <span className="sr-only">Priorität {PRIORITY_NAME[card.priority]}</span>
        </span>
      )}
      {!compact && card.hasDescription && <AlignLeft size={13} aria-label="Hat Beschreibung" />}
      {card.checklistTotal > 0 && (
        <span
          className={cx(
            'inline-flex items-center gap-1',
            card.checklistDone === card.checklistTotal && 'rounded bg-emerald-600 px-1 text-white',
          )}
          title="Checkliste"
        >
          <CheckSquare size={12} /> {card.checklistDone}/{card.checklistTotal}
        </span>
      )}
      {!compact && card.commentCount > 0 && (
        <span className="inline-flex items-center gap-1" title="Kommentare">
          <MessageSquare size={12} /> {card.commentCount}
        </span>
      )}
      {!compact && card.attachmentCount > 0 && (
        <span className="inline-flex items-center gap-1" title="Anhänge">
          <Paperclip size={12} /> {card.attachmentCount}
        </span>
      )}
      {assignees.length > 0 && (
        <span className="ml-auto flex -space-x-1.5">
          {assignees.slice(0, 3).map((m) =>
            m.id === card.ownerId && assignees.length > 1 ? (
              <span key={m.id} className="relative" title={`${m.name} (hauptverantwortlich)`}>
                <Avatar user={m} size={compact ? 18 : 22} />
                <Star size={10} className="absolute -right-0.5 -bottom-0.5 fill-amber-400 text-amber-500" aria-label="hauptverantwortlich" />
              </span>
            ) : (
              <Avatar key={m.id} user={m} size={compact ? 18 : 22} />
            ),
          )}
          {assignees.length > 3 && (
            <span className="inline-flex size-[22px] items-center justify-center rounded-full bg-slate-500 text-[10px] text-white ring-2 ring-[var(--panel)]">
              +{assignees.length - 3}
            </span>
          )}
        </span>
      )}
    </div>
  );
}
